const SPLIT_NUMBER = 200;

/**
 * Build a sparse axis-label configuration for an ECharts gauge.
 *
 * Accepts pre-computed label entries (gauge position + display text) and returns
 * a splitNumber + formatter that shows only those labels via tolerance matching.
 *
 * @param {{ gaugeValue: number, displayText: string, priority?: number }[]} labels
 * @param {number} min  Gauge minimum value
 * @param {number} max  Gauge maximum value
 * @returns {{ splitNumber: number, formatter: (value: number) => string }}
 */
export function buildSparseGaugeLabels(labels, min, max) {
  const range = Math.abs(max - min);
  if (range === 0) {
    return { splitNumber: SPLIT_NUMBER, formatter: () => "" };
  }

  const tolerance = range / (2 * SPLIT_NUMBER);

  // Filter out invalid entries
  const valid = labels.filter(
    (l) =>
      Number.isFinite(l.gaugeValue) &&
      l.displayText != null &&
      String(l.displayText) !== "NaN" &&
      String(l.displayText) !== "undefined" &&
      String(l.displayText) !== "Infinity" &&
      String(l.displayText) !== "-Infinity"
  );

  // Sort by gauge value
  const sorted = [...valid].sort((a, b) => a.gaugeValue - b.gaugeValue);

  // Deduplicate: when two labels occupy effectively the same gauge position
  // (within tolerance), keep the one with higher priority.
  const deduped = [];
  for (const label of sorted) {
    const existingIdx = deduped.findIndex(
      (d) => Math.abs(d.gaugeValue - label.gaugeValue) <= tolerance
    );
    if (existingIdx !== -1) {
      if ((label.priority || 0) > (deduped[existingIdx].priority || 0)) {
        deduped[existingIdx] = label;
      }
    } else {
      deduped.push(label);
    }
  }

  return {
    splitNumber: SPLIT_NUMBER,
    formatter: (value) => {
      for (const label of deduped) {
        if (Math.abs(value - label.gaugeValue) <= tolerance) {
          return String(label.displayText);
        }
      }
      return "";
    },
  };
}
